import {httpHandler} from '../../../../util/alme-api';
import moment, { Moment } from "moment";
import { Store } from "redux";
import { VNode } from "virtual-dom";
import h from 'virtual-dom/h';
import { DefaultSettings, settings } from '../../../../settings/settings-util';
import { AlmeState } from "../../../../wireup/AlmeState";
import { DateConfig, DayClickEvent, MetaData, SaveDateTimeFun, LazyLoadMetaData } from "../models/CalendarWidgetConfig";

export default class CalendarWidget
{
    _settings: DefaultSettings;
    _store: Store<AlmeState> | null = null;
    _incomingDateFormat: string;
    _outgoingDateFormat: string;
    _incomingTimeFormat: string;
    _outgoingTimeFormat: string;
    enableTimePicker: boolean;
    _dateConfig?: DateConfig[] | null;
    _availableDates: Moment[] | undefined;
    _selectedDate: Moment | undefined;
    _lazyLoad: LazyLoadMetaData | undefined;
    _processedLazyLoad = false;
    saveDate?: SaveDateTimeFun;
    saveTime?: SaveDateTimeFun;
    _apiError: boolean;

    //#region Public Interface
    constructor(passedSettings: DefaultSettings)
    {
        this._settings = passedSettings || settings;

        this._incomingDateFormat = this._settings.CalendarWidget &&
            this._settings.CalendarWidget.incomingDateFormat ?
            this._settings.CalendarWidget.incomingDateFormat :
            settings.CalendarWidget.incomingDateFormat;

        this._outgoingDateFormat = this._settings.CalendarWidget &&
            this._settings.CalendarWidget.outgoingDateFormat ?
            this._settings.CalendarWidget.outgoingDateFormat :
            settings.CalendarWidget.outgoingDateFormat;

        this._incomingTimeFormat = this._settings.CalendarWidget &&
            this._settings.CalendarWidget.incomingTimeFormat ?
            this._settings.CalendarWidget.incomingTimeFormat :
            settings.CalendarWidget.incomingTimeFormat;

        this._outgoingTimeFormat = this._settings.CalendarWidget &&
            this._settings.CalendarWidget.outgoingTimeFormat ?
            this._settings.CalendarWidget.outgoingTimeFormat :
            settings.CalendarWidget.outgoingTimeFormat;

        this.enableTimePicker = true;
        this._apiError = false;
    }

    configure(metadata: MetaData, saveDate?: SaveDateTimeFun, saveTime?: SaveDateTimeFun): void
    {
        if (this._dateConfig) return;

        if(!saveTime){
            this.enableTimePicker = false;
        }
        const startDate = metadata?.startDate;
        const endDate = metadata?.endDate;
        const defaultSelectedDate = metadata?.defaultSelectedDate;
        const exceptionDates = metadata?.exceptionDates ?? [];
        if(metadata?.lazyLoad?.enabled === true){
            this._lazyLoad = metadata?.lazyLoad;
        }
        else{
            this._lazyLoad= {
                enabled: false,
            };
        }

        const lang = this._settings.languageConstants.languages[this._settings.languageConstants.default];
        this._availableDates = [];

        this.saveDate = saveDate;
        this.saveTime = saveTime;

        if(!this._lazyLoad.enabled){
            if (startDate && endDate) {
                this._dateConfig = this.buildDatesFromStartDateAndEndDate(startDate, endDate, exceptionDates);
             } 
             else {
                 this._dateConfig = metadata?.dates;
                 this._dateConfig?.sort((date1, date2) => parseInt(date1.date) - parseInt(date2.date));
             }
             
            this._availableDates = this._dateConfig?.map(date => moment(date.date, this._incomingDateFormat, lang)) ?? [];

            this.setDefaultSelectedDate(defaultSelectedDate);
            
            setTimeout(this.onFirstRender.bind(this), 0);
        }
        else{
            if(this._processedLazyLoad === false){
                this._processedLazyLoad = true;
            
                let start = moment();
                if(startDate !== undefined){
                    start = moment(startDate, this._incomingDateFormat, lang);
                }
                
                this.buildDatesFromExtensibleRequest(this.getMonthValueFromMoment(start), start.year().valueOf(), defaultSelectedDate, 3, true);

                if(this._lazyLoad.allowPastTenseDates){
                    start = start.add(-1, "months");

                    this.buildDatesFromExtensibleRequest(this.getMonthValueFromMoment(start), start.year().valueOf(), defaultSelectedDate);
                }
                
            }
            
        }     
    }

    setDefaultSelectedDate(defaultSelectedDate: string|unknown): void{
        const lang = this._settings.languageConstants.languages[this._settings.languageConstants.default];

        if(this._availableDates === undefined){
            return;
        }

        if (defaultSelectedDate) {
            const momDefaultSelectedDate = moment(
                defaultSelectedDate,
                this._incomingDateFormat,
                lang
            );
            if (this._availableDates?.some((date) =>date.isSame(momDefaultSelectedDate))) {
                this._selectedDate = momDefaultSelectedDate;
            }
            else {
                this._selectedDate = this._availableDates[0];
            }
        } 
        else {
            this._selectedDate = this._availableDates[0];
        }
          
    }

    clearConfig(): void
    {
        this._dateConfig = null;
        this.enableTimePicker = true;
        this._processedLazyLoad = false;
        this._apiError = false;
        this._lazyLoad = undefined;
        this._selectedDate = undefined;
    }

    render(): VNode | null
    {
        if(this._apiError){
            const errors = h('div', { className: this._settings.classNames.calendarWidgetBodyError }, this._lazyLoad?.errorText ?? "An error has occurred");
            return h('div', { className: this._settings.classNames.calendarWidgetBody }, [errors]);
        }

        if (!this._selectedDate){
            return null;
        } 

        const index = this.selectedDateIndex() ?? -1;
        const calendarWrapper = this.buildCalendarWrapper(this._selectedDate);

        let body = h('div', { className: this._settings.classNames.calendarWidgetBody }, [calendarWrapper]);
        if(this.enableTimePicker){
            let times = this._dateConfig?.[index]?.times ?? null;

            if(this._lazyLoad?.enabled && !times && this._dateConfig){
                //populate times BY POPULATING DATECONFIG

                const datesToLookup: string[] = [];

                for (let i = 0; i < 3; i++) {
                    if(this._dateConfig?.[index + i]?.date){
                        datesToLookup.push(this._dateConfig?.[index + i].date);
                    }
                }

                this.getTimeInfo(datesToLookup);

                if(this._dateConfig?.[index].date)
                {
                    this._dateConfig[index].times = [];
                }
                
                times = [];
            }
            else{
                if(!times){
                    times = [];
                }
            }
            
            const timePicker = this.buildTimePicker(times);

            body = h('div', { className: this._settings.classNames.calendarWidgetBody }, [calendarWrapper, timePicker]);
        }      

        const properties =
        {
            ariaLabel: this._settings.accessibility.aria.calendarWidgetLabel,
            className: this._settings.classNames.calendarWidget,
            role: "application",
        };
        return h('div', properties , [body]);
    }

    //#endregion



    //#region Build Header Methods

    buildHeader(date: Moment): VNode
    {
        const prevMonth = this.buildPreviousMonth(date);
        const nextMonth = this.buildNextMonth(date);
        const title = this.buildHeaderTitle(date);

        const properties =
        {
            className: this._settings.classNames.calendarWidgetHeader,
        };

        return h('div', properties, [prevMonth, title, nextMonth]);
    }

    buildHeaderTitle(date: Moment): VNode
    {
        const properties =
        {
            className: this._settings.classNames.calendarWidgetHeaderTitle,
            id: "calendar-widget-title",
        };

        return h('div', properties, date.format("MMMM YYYY"));
    }

    buildPreviousMonth(date: Moment): VNode
    {
        const properties: Record<string, unknown> =
        {
            ariaLabel: `${this._settings.accessibility.aria.calendarWidgetPreviousMonthLabel} ${date.clone().subtract("1", "months").format("MMMM YYYY")}`,
            className: this._settings.classNames.calendarWidgetHeaderPrevMonth,
            onclick: this.buildPrevMonthOnClick(),
        };

        if (!this._availableDates?.find(availableDate => availableDate.isBefore(date, "month")))
        {
            properties.disabled = true;
        }

        return h('button', properties, []);
    }

    buildNextMonth(date: Moment): VNode
    {  
        const properties: Record<string, unknown> =
        {
            ariaLabel: `${this._settings.accessibility.aria.calendarWidgetNextMonthLabel} ${date.clone().add("1", "months").format("MMMM YYYY")}`,
            className: this._settings.classNames.calendarWidgetHeaderNextMonth,
            onclick: this.buildNextMonthOnClick(),
        };

        if (!this._availableDates?.find(availableDate => availableDate.isAfter(date, "month")))
        {
            properties.disabled = true;
        }

        return h('button', properties, []);
    }

    //#endregion



    //#region Build Calendar Methods

    buildCalendarWrapper(date: Moment): VNode
    {
        const header = this.buildHeader(date);
        const table = this.buildTable(date);

        return h('div', { className: this._settings.classNames.calendarWidgetCalendarWrapper }, [header, table]);
    }

    buildTable(date: Moment): VNode
    {
        const header = this.buildTableHeader();
        const body = this.buildTableBody(date);

        const properties =
        {
            className: this._settings.classNames.calendarWidgetTable,
            role: "grid",
            ariaLabelledBy: "calendar-widget-title",
        };

        return h('table', properties, [header, body]);
    }

    buildTableHeader(): VNode
    {
        const daysOfWeek =
        {
            Sunday: this._settings.textConstants.calendarWidgetSundayText,
            Monday: this._settings.textConstants.calendarWidgetMondayText,
            Tuesday: this._settings.textConstants.calendarWidgetTuesdayText,
            Wednesday: this._settings.textConstants.calendarWidgetWednesdayText,
            Thursday: this._settings.textConstants.calendarWidgetThursdayText,
            Friday: this._settings.textConstants.calendarWidgetFridayText,
            Saturday: this._settings.textConstants.calendarWidgetSaturdayText,
        };

        const headerRow = h('tr', {}, Object.keys(daysOfWeek).map(key =>
        {
            const abbr = h('abbr', { ariaLabel: key }, daysOfWeek[key]);
            return  h('th', { scope: 'col' }, [abbr]);
        }));

        return h('thead', {}, [headerRow]);
    }

    buildTableBody(date: Moment): VNode
    {
        const rows = this.generateDatesForCalendarMonth(date).map(this.buildTableRow.bind(this));

        return h('tbody', {}, rows);
    }

    buildTableRow(dates: Moment[]): VNode
    {
        return h('tr', { className: this._settings.classNames.calendarWidgetRow }, dates.map(this.buildTableDay.bind(this)));
    }

    buildTableDay(day: Moment): VNode
    {
        if (!day)
        {
            return h('td', {className: this._settings.classNames.calendarWidgetDayEmpty}, []);
        }
        const properties: Record<string, unknown> =
        {
            ariaLabel: day.format("D MMMM YYYY dddd"),
            className: this._settings.classNames.calendarWidgetDayButton,
            onclick: this.buildDayOnClick(),
            onkeydown: this.buildDayOnKeyDown(),
            submitDate: day.format(this._outgoingDateFormat),
            tabIndex: -1,
        };

        if (!this._availableDates?.find(date => date.isSame(day, "day")))
        {
            properties.disabled = true;
        }

        if (day.isSame(this._selectedDate, "day"))
        {
            properties.className += ` ${this._settings.classNames.calendarWidgetDayButtonSelected}`;
            properties.tabIndex = 0;
        }

        const button = h('button', properties, day.date().toString());

        return h('td', {}, [button]);
    }

    //#endregion



    //#region Build Time Picker Methods

    buildTimePicker(times: string[]): VNode
    {
        const properties =
        {
            ariaLabel: this._selectedDate?.format("D MMMM dddd"),
            className: this._settings.classNames.calendarWidgetTimeSelector,
        };

        return h('div', properties, times.map(this.buildTimeButton.bind(this)));        
    }

    buildTimeButton(timeString: string): VNode
    {
        const lang = this._settings.languageConstants.languages[this._settings.languageConstants.default];
        const timeMoment = moment(timeString, this._incomingTimeFormat, lang);
        const year = this._selectedDate?.year();
        const month = this._selectedDate?.month();
        const date = this._selectedDate?.date();
        if (year)
            timeMoment.year(year);
        if (month)
            timeMoment.month(month);
        if (date)
            timeMoment.date(date);

        const properties =
        {
            ariaLabel: timeMoment.format("h:mm a D MMMM dddd"),
            className: this._settings.classNames.calendarWidgetTimeButton,
            onclick: this.buildTimeOnClick(),
            submitTime: timeMoment.format(this._outgoingTimeFormat),
        };

        return h('button', properties, timeMoment.format("hh:mm a"));
    }

    //#endregion



    //#region Build Event Methods

    buildDayOnKeyDown()
    {
        return (event: KeyboardEvent): void =>
        {
            switch (event.key)
            {
                case "ArrowLeft":
                case "Left":
                    event.preventDefault();
                    this.moveLeft();
                    break;

                case "ArrowUp":
                case "Up":
                    event.preventDefault();
                    this.moveUp();
                    break;

                case "ArrowRight":
                case "Right":
                    event.preventDefault();
                    this.moveRight();
                    break;

                case "ArrowDown":
                case "Down":
                    event.preventDefault();
                    this.moveDown();
                    break;
            }
        };
    }

    buildPrevMonthOnClick()
    {
        return (): void =>
        {
            this.deselectDay();
            this.deselectTime();

            let previousDates: Moment[] | undefined = undefined;

            previousDates = this._availableDates?.filter(availableDate => availableDate.isBefore(this._selectedDate, "month"));

            if(this._lazyLoad?.enabled){
                const lastMonth = this._selectedDate?.add(-2, "month");

                if(lastMonth !== undefined && !this._availableDates?.find(availableDate => availableDate.isBefore(lastMonth, "month")))
                {
                    if(this._lazyLoad.allowPastTenseDates || lastMonth.isAfter(moment())){
                        this.buildDatesFromExtensibleRequest(this.getMonthValueFromMoment(lastMonth), lastMonth.year(), undefined);
                    } 
                }
            }

            this._selectedDate = previousDates? previousDates[previousDates.length - 1] : this._selectedDate;

            this.saveDate?.(this._selectedDate?.format(this._outgoingDateFormat) ?? "");
            if(this.enableTimePicker){
                this.saveTime?.("");
            }  
        };
    }

    buildNextMonthOnClick()
    {
        return (): void =>
        {
            this.deselectDay();
            this.deselectTime();

            const nextDates = this._availableDates?.filter(availableDate => availableDate.isAfter(this._selectedDate, "month"));

            if(this._lazyLoad?.enabled){
                const nextMonth = this._selectedDate?.add(2, "month");
                if(nextMonth !== undefined && !this._availableDates?.find(availableDate => availableDate.isAfter(nextMonth, "month")))
                {
                    this.buildDatesFromExtensibleRequest(this.getMonthValueFromMoment(nextMonth), nextMonth.year(), undefined);
                }
            }
             
            this._selectedDate = nextDates? nextDates[0] : this._selectedDate;
            this.saveDate?.(this._selectedDate?.format(this._outgoingDateFormat) ?? "");
            if(this.enableTimePicker){
                this.saveTime?.("");
            }  
        };
    }

    buildDayOnClick()
    {
        return (event: DayClickEvent): void =>
        {
            const className = this._settings.classNames.calendarWidgetDayButtonSelected;
            const lang = this._settings.languageConstants.languages[this._settings.languageConstants.default];

            if (!event?.target?.classList.contains(className))
            {
                this.deselectDay();
                this.deselectTime();
                event?.target?.classList.add(className);
                this._selectedDate = moment(event?.target?.submitDate, this._outgoingDateFormat, lang);
                this.saveDate?.(event?.target?.submitDate ?? "");

                if(this.enableTimePicker){
                    this.saveTime?.("");
                }  

                //Bugfix for Safari: Button won't focus on click if tabindex is -1
                event?.target?.focus();
            }
        };
    }

    buildTimeOnClick()
    {
        return (event: DayClickEvent): void =>
        {
            this.deselectTime();
            event?.target?.classList.add(this._settings.classNames.calendarWidgetTimeButtonSelected);
            this.saveTime?.(event?.target?.submitTime ?? "");
        };
    }

    //#endregion



    //#region Calendar Navigation Methods

    moveLeft(): void
    {
        
        const selectedIndex = this.selectedDateIndex();
        
        this._selectedDate = selectedIndex && this._availableDates ?
            this._availableDates[selectedIndex - 1] :
            this._selectedDate;

        this.saveDate?.(this._selectedDate?.format(this._outgoingDateFormat) ?? "");

        if(this.enableTimePicker){
            this.saveTime?.("");
        }  

        setTimeout(() => this.focusSelectedDay(), 0);
    }

    moveUp(): void
    {
        const previousWeek = this._selectedDate?.clone().subtract(1, "weeks");
        const options = this._availableDates?.filter(date => date.isSameOrBefore(previousWeek, "day"));


        this._selectedDate = options && options.length ?
            options[options.length - 1] :
            this._availableDates?.[0];

        this.saveDate?.(this._selectedDate?.format(this._outgoingDateFormat) ?? "");
        if(this.enableTimePicker){
            this.saveTime?.("");
        }  

        setTimeout(() => this.focusSelectedDay(), 0);
    }

    moveRight(): void
    {
        const selectedIndex = this.selectedDateIndex();
        if (this._availableDates && selectedIndex != undefined) {
            this._selectedDate = selectedIndex === this._availableDates.length - 1 ?
                this._selectedDate : this._availableDates[selectedIndex + 1];
        }

        this.saveDate?.(this._selectedDate?.format(this._outgoingDateFormat) ?? "");
        if (this.enableTimePicker && this.saveTime) {
            this.saveTime("");
        }

        setTimeout(() => this.focusSelectedDay(), 0);
    }

    moveDown(): void
    {
        const nextWeek = this._selectedDate?.clone().add(1, "weeks");
        const options = this._availableDates?.filter(date => date.isSameOrAfter(nextWeek, "day"));

        if(options && this._availableDates) {
            this._selectedDate = options.length ?
            options[0] :
            this._availableDates[this._availableDates.length - 1];
        }

        this.saveDate?.(this._selectedDate?.format(this._outgoingDateFormat) ?? "");
        if(this.enableTimePicker){
            this.saveTime?.("");
        }  

        setTimeout(() => this.focusSelectedDay(), 0);
    }

    //#endregion



    //#region Helper Methods

    generateDatesForCalendarMonth(dateInMonth: Moment): Moment[][]
    {
        const startOfMonth = dateInMonth.clone().startOf("month");
        const endOfMonth = dateInMonth.clone().endOf("month");
        const firstDay = startOfMonth.day();

        const currentDate = startOfMonth.clone();
        let addBlanks = true;

        const weeks: Moment[][] = [];

        while (!currentDate.isSame(endOfMonth, "day"))
        {
            weeks.push(Array.from(Array(7), (element, index) =>
            {
                if (index === firstDay && addBlanks && currentDate.isSame(startOfMonth, "day"))
                {
                    addBlanks = false;
                    return currentDate.clone();
                }

                if (currentDate.isSame(endOfMonth, "day")) addBlanks = true;

                if (addBlanks) return element;

                return currentDate.add(1, "days").clone();
            }));
        }

        return weeks;
    }

    // Converts 0 indexed moment to the correct month number
    getMonthValueFromMoment(date: Moment): number
    {
        return date.month().valueOf() + 1;
    }

    selectedDateIndex(): number | undefined
    {
        return this.specificDateIndex(this._selectedDate);
    }

    specificDateIndex(searchDate: Moment | undefined): number | undefined {
        return this._availableDates?.findIndex(date => date.isSame(searchDate, "day"));
    }

    deselectDay(): void
    {
        const selectedClassName = this._settings.classNames.calendarWidgetDayButtonSelected;

        const selectedButton = document.querySelector(`.${selectedClassName}`);
        if (selectedButton) selectedButton.classList.remove(selectedClassName);
    }

    deselectTime(): void
    {
        const selectedClassName = this._settings.classNames.calendarWidgetTimeButtonSelected;

        const selectedButton = document.querySelector(`.${selectedClassName}`);
        if (selectedButton) selectedButton.classList.remove(selectedClassName);
    }

    focusSelectedDay(): void
    {
        const selectedClassName = this._settings.classNames.calendarWidgetDayButtonSelected;
        const selectedButton: HTMLButtonElement | null = document.querySelector(`.${selectedClassName}`);

        if (selectedButton) selectedButton.focus();
    }

    onFirstRender(): void
    {
        const calendarPane = document.querySelector(`.${this._settings.classNames.calendarPane}`);
        const focusableElement: HTMLLinkElement | null | undefined = calendarPane?.querySelector('a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]');
        if (focusableElement) focusableElement.focus();

        this.saveDate?.(this._selectedDate?.format(this._outgoingDateFormat) ?? "");
        if(this.enableTimePicker){
            this.saveTime?.("");
        }  
    }

    buildDatesFromStartDateAndEndDate(startDate: string, endDate: string, exceptionDateList: DateConfig[]): DateConfig[] {
        const lang = this._settings.languageConstants.languages[this._settings.languageConstants.default];
        const exceptionDates = exceptionDateList.map(date => moment(date.date, this._incomingDateFormat, lang));
        const start = moment(startDate, this._incomingDateFormat, lang);
        const end = moment(endDate, this._incomingDateFormat, lang);
        const dates: DateConfig[] = [];

        while (start.isSameOrBefore(end)) {
            const exceptionDate = exceptionDates.find(date => date.isSame(start, "day"));
            if(!exceptionDate) {
                const date: DateConfig = {
                    date: start.format(this._incomingDateFormat),
                };
                dates.push(date);
            }
            start.add(1, "days");
        }
        return dates;
    }

    buildDatesFromExtensibleRequest(month: number, year: number, defaultSelected: string | undefined, reattemptAmount = 0, populateNextMonth = false): void{

        const params: Record<string, string> = { 
            "month": month.toString(),
            "year": year.toString(),
        };

        if(this._lazyLoad?.customData !== undefined){
            params["customData"] = encodeURIComponent(JSON.stringify(this._lazyLoad?.customData));
        }

        try{
            if(this._lazyLoad?.datesEndpoint === undefined){
                throw new Error("Missing Times Endpoint Value!");
            }

            this.GetCalendarData(this._lazyLoad?.datesEndpoint, params).then(
                (value) => {
                    if(value === null){
                        this.setupLazyLoadErrorHandling(new Error("Invalid Response"));
                        return;
                    }
                    const valueList = value as string[];
                    if(valueList?.length > 0){
                        this.setupLazyLoadDatesCallback(valueList, defaultSelected);
                        if(populateNextMonth){
                            month = month + 1;
                            if(month > 12){
                                year = year + 1;
                                month = 1;
                            }

                            this.buildDatesFromExtensibleRequest(month, year, defaultSelected);
                        }
                    }
                    else{
                        if(reattemptAmount > 0){
                            month = month + 1;
                            if(month > 12){
                                year = year + 1;
                                month = 1;
                            }
                            this.buildDatesFromExtensibleRequest(month, year, defaultSelected, reattemptAmount - 1, populateNextMonth);
                        }
                    }
                    
                },
                (reject) => {
                    this.setupLazyLoadErrorHandling(new Error(reject));
                }
            );  
        }
        catch(error){
            this.setupLazyLoadErrorHandling(error);
        }  
    }

    setupLazyLoadDatesCallback(dateArray: string[], defaultSelected?: string | undefined): void{
        if(!this._dateConfig){
            this._dateConfig = [];
        }

        dateArray.forEach( (element) => {
            if (!this._dateConfig?.some(e => e.date === element)) {
                this._dateConfig?.push({
                    "date": element,
                });
              }
        });

        this._availableDates = this._dateConfig?.map(date => moment(date.date, "MM/DD/YYYY")) ?? [];

        if(this._selectedDate === undefined){
            this.setDefaultSelectedDate(defaultSelected);
        }
        

        setTimeout(this.onFirstRender.bind(this), 0);
    }


    getTimeInfo(dates: string[]): void{
        
        const params: Record<string, string> = { 
            "dates": encodeURIComponent(dates?.toString()),
        };

        if(this._lazyLoad?.customData !== undefined){
            params["customData"] = encodeURIComponent(JSON.stringify(this._lazyLoad?.customData));
        }

        try{
            if(this._lazyLoad?.timesEndpoint === undefined){
                throw new Error("Missing Times Endpoint Value!");
            }

            this.GetCalendarData(this._lazyLoad?.timesEndpoint, params).then(
                (value) => {
                        this.setupLazyLoadTimesCallback(value as Record<string, string[]>);
                    },
                (reject) => {
                        this.setupLazyLoadErrorHandling(new Error(reject));
                    }
                );
        } 
        catch(error){
            this.setupLazyLoadErrorHandling(error);
        }  
    }

    setupLazyLoadTimesCallback(value: Record<string, string[]>): void{
        for (const key in value) {
            const date = moment(key, settings.CalendarWidget.incomingDateFormat);
            const index = this.specificDateIndex(date);
            if(index !== undefined && index > -1 && this._dateConfig?.[index]){
                this._dateConfig[index].times = value[key];
            }
        }
    }

    setupLazyLoadErrorHandling(error): void{
        console.log(error);
        this._apiError = true;
    }

    async GetCalendarData(endpoint: string, parameters?: Record<string, string>): Promise<unknown>{
        let url = this._settings.AlmeAPI.ALME_API_PROTOCOL + this._settings.AlmeAPI.ALME_API_HOSTNAME + this._settings.AlmeAPI.ALME_API_BASE_PATH + endpoint;

        if(parameters){
            let paramString = "?";
            for (const key in parameters) {
                const value = parameters[key];
                paramString = `${paramString}${key}=${value}&`;
            }

            url = `${url}${paramString.substring(0, paramString.length - 1)}`;
        }

        return await httpHandler.SendAsync(
            url,
            {method: "GET"}
        );
    }
    //#endregion
}


// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/window-feedback-overlay/src/elements/calendarWidget.ts